import { useNavigate, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { Input } from "reactstrap";

import useCloudPermissions, {
  CloudPermissions,
} from "../../hooks/useCloudPermissions";
import CollapsibleGroup from "./CreateCloudView/CreateCloudComponents/CollapsibleGroup";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";
import Constants from "../../utils/Constants";

interface SectionType {
  title: string;
  description: string;
  icon: string;
  link: string;
  disabled?: boolean;
}

export const Sections = (cPermissions: CloudPermissions): SectionType[] => [
  {
    title: "Settings",
    description: "View & manage all core cloud features, edit cloud name",
    icon: "mdi mdi-cog",
    link: "settings/globals/Tags",
    disabled: !cPermissions.cloud_create_global_quick_create,
  },
  {
    title: "Security & Access",
    description: "View & manage team members in this GrowthCloud",
    icon: "mdi mdi-shield-account",
    link: "settings/security-access/users",
    disabled: !cPermissions.cloud_create_person,
  },
  {
    title: "Data Sources",
    description: "Create and manage data sources available in your Cloud",
    icon: "mdi mdi-database-cog-outline",
    link: "settings/modules",
    disabled: !cPermissions.cloud_create_module,
  },
  {
    title: "Recycle Bin",
    description: "Manage deleted records in this Cloud",
    link: "settings/recycle-bin",
    icon: "mdi mdi-delete-outline",
    disabled: !cPermissions.cloud_modify_deleted_records,
  },
  {
    title: "Sharing Center",
    description:
      "Manage all the records, views, pages, record pages you shared or subscribed to in this Cloud",
    icon: "mdi mdi-share-variant",
    link: "settings/sharing-center/SharedRecords",
    // TODO: Define which permission use here. As well as Other sharing permissions
    disabled: false,
  },
  {
    title: "Cloud Public Forms",
    description: "Manage all cloud public forms",
    icon: "mdi mdi-form-select",
    link: "settings/publicForms",
    disabled: !cPermissions.cloud_get_credentials,
  },
  {
    title: "Cloud Backup",
    description: "Manage or restore previous cloud backups",
    icon: "mdi mdi-database-sync",
    link: "settings/backup/main",
    disabled: !cPermissions.cloud_get_credentials,
  },
  {
    title: "Workflows",
    description: "Create and manage your cloud workflows",
    icon: "mdi mdi-sitemap",
    link: "workflows",
    disabled: !cPermissions.cloud_get_workflow,
  },
  {
    title: "Bulk Email Actions",
    description: "Create and manage your bulk email actions",
    icon: "mdi mdi-email-fast-outline",
    link: "emails",
    disabled: !cPermissions.cloud_get_workflow,
  },
  {
    title: "Workspaces",
    description: "Manage this cloud workspaces",
    icon: "mdi mdi-view-dashboard-outline",
    link: "workspaces",
    disabled: !cPermissions.cloud_get_workspaces,
  },
  {
    title: "API Keys",
    description: "Create and manage the API keys in your Cloud",
    icon: "mdi mdi-api",
    link: "settings/api-keys",
    disabled: !cPermissions.cloud_get_credentials,
  },
];

const CloudSettingsHome = () => {
  const navigate = useNavigate();
  const tenantID = useAtomValue(tenantIDAtom);
  const [searchValue, setSearchValue] = useState("");
  const { cPermissions } = useCloudPermissions();

  const finalSections = useMemo(() => {
    const isSupportal = tenantID === Constants.SUP_CLOUD_ID;

    let sections = Sections(cPermissions);
    if (isSupportal)
      sections = sections.filter((s) =>
        ["Settings", "Security & Access", "API Keys"].includes(s.title)
      );

    return sections;
  }, [cPermissions, tenantID]);

  const filteredSections = finalSections.filter(
    searchValue ? filterSections : () => true
  );

  return (
    <>
      <AppHelmet title="Cloud Settings - GrowthCloud.com" />
      <div className="p-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap px-3 border-bottom pb-2 mb-3">
          <div className="d-flex">
            <i
              className="mdi mdi-cloud-outline text-primary  mr-3"
              style={{ fontSize: 36 }}
            />
            <div>
              <h2 className="mb-0 ">Cloud Settings</h2>
              <small>All settings related to your cloud</small>
            </div>
          </div>
          <BasicSearch setSearchValue={setSearchValue} />
        </div>
        {filteredSections.length > 0 ? (
          filteredSections.map((section, i) => {
            return (
              <CollapsibleGroup
                key={i}
                title={section.title}
                description={section.description}
                collapsible={false}
                icon={section.icon}
                onClick={() => navigate(`/clouds/${tenantID}/${section.link}`)}
                disabled={section.disabled}
                disabledMessage="You don't have sufficient permissions to access this page."
              />
            );
          })
        ) : (
          <div className="px-4 py-2 border bg-ultralight rounded text-dark font-weight-500">
            No Results Found.
          </div>
        )}
      </div>
    </>
  );

  function filterSections(section: SectionType) {
    const lowercaseTitle = section.title.toLowerCase();
    const lowercaseDescription = section.description.toLowerCase();
    const lowercaseSearch = searchValue.toLowerCase();

    return (
      lowercaseTitle.includes(lowercaseSearch) ||
      lowercaseDescription.includes(lowercaseSearch)
    );
  }
};

export function BasicSearch({
  searchValue,
  setSearchValue,
  placeholder = "Search settings",
  useQueryParams = false,
}: {
  searchValue?: string;
  setSearchValue: (newVal: string) => void;
  placeholder?: string;
  useQueryParams?: boolean;
}) {
  const [params, setParams] = useSearchParams();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    // Replace url with updated url params
    if (useQueryParams) {
      if (!val) {
        setParams(new URLSearchParams());
      } else {
        params.set("search", val);
        setParams(params);
      }
    }

    setSearchValue(val);
  };

  return (
    <div
      className="d-flex align-items-center"
      style={{ maxWidth: 350, width: "100%" }}
    >
      <i className="mdi mdi-magnify mr-2" style={{ fontSize: 24 }} />
      <Input
        bsSize="sm"
        placeholder={placeholder}
        value={searchValue || undefined}
        onChange={onChange}
        style={{ height: 36 }}
      />
    </div>
  );
}

export default CloudSettingsHome;
